import React, { useState, useEffect } from 'react';
import {
  Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, IconButton, Button, Typography, Box, LinearProgress
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import DataQualityMain from './DataQualityMain';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import {  Check, Save } from 'lucide-react';
import { toast, Toaster } from 'react-hot-toast';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import CloseIcon from '@mui/icons-material/Close';
import Plot from 'react-plotly.js';
import heartLoading from "../../../assests/heartLoading.gif";
import preloader from "../../../assests/preloader1.gif";
import Tooltip from '@mui/material/Tooltip';


export default function DataQuality({onPrevious, nextStep, id, userRole}) {
  
    const [openModal, setOpenModal] = useState(false);
    const [openResultModal, setOpenResultModal] = useState(false);
    const [openRuleSuggestionsModal, setOpenRuleSuggestionsModal] = useState(false);
    const [assetId, setAssetId] = useState("");
    const [assetName, setAssetName] = useState("");
    const [assets, setAssets] = useState([]);
    const [loading, setLoading] = useState(false)
    const [dataQualityResults, setDataQualityResults] = useState();
    const [error, setError] = useState("");
    const [finalResult, setFinalResult] = useState(false);
    const [dqResultAsset, setDQResultAsset] = useState({});
    const [totalRecords, setTotalRecords] = useState(0);
    const [completedRecords, setCompletedRecords] = useState(0);
    const [qualityScore, setQualityScore] = useState(0);
    const [weightage, setWeightage] = useState(null);
    const [chartData, setChartData] = useState(null);
    const [ruleSuggestions, setRuleSuggestions] = useState([]);
    const [ruleSuggestionsLoading, setRuleSuggestionsLoading] = useState(false);
    const [dqJobStatusLoading, setDqJobStatusLoading] = useState(false);
    const [runDqLoading, setRunDqLoading] = useState(false);
    const [hasResultsFetched, setHasResultsFetched] = useState(false);

    const getQualityScoreColor = (score) => {
    if (score > 85) return '#4CAF50';
    if (score >= 55) return '#FF9800';
    if (score < 55) return '#F44336';
    return '#000';
    };
  
    useEffect(() => {
      setLoading(true)
      const BASE_URL = process.env.REACT_APP_API_URL;
      axios.get(`${BASE_URL}/asset-list/${id}`)
        .then(res => {
              const fetchedAssets = res.data;
              setAssets(fetchedAssets);
              pollStatus(fetchedAssets);
            })
        .catch(err => { 
          setAssets([]);
          setLoading(false);
        });    
    }, [id]);

    const fetchDQJobResults = () => {
      if (hasResultsFetched) return;
      
      try {
        axios.get(`${process.env.REACT_APP_API_URL}/runQuality/ruleStatus/${id}`)
        .then(response => {
        if (response?.status === 200 || response?.status === 201) {
          setDataQualityResults(response?.data);
          setFinalResult(true);
          setHasResultsFetched(true);
          setLoading(false);
        }
      })
      } catch (err) {
          setError("Failed to load profiling results.");
          setHasResultsFetched(true);
          setLoading(false);
      }
      };
     
    const pollStatus = (assets) => {
        try {
            axios.get(`${process.env.REACT_APP_API_URL}/runQuality/status/${id}`)
            // Update job status
            .then(response => 
              {
                if (response?.data?.overall_status == "Success" || response?.data?.overall_status == "Failed") {
                const assetList = assets?.map(as =>{
                  const matchedAsset = response?.data?.dq_jobs?.find(job => job.data_asset_name === as.name);
                  if(matchedAsset) 
                    return{...as, dq_job_status: matchedAsset.status};
                  return as;
                });
                setAssets(assetList);
                setDqJobStatusLoading(false);
                fetchDQJobResults();
            } else if (response?.data?.overall_status === "In Progress") {
                setLoading(false);
                setDqJobStatusLoading(true);
                // Continue polling every 60 seconds
                setTimeout(() => pollStatus(assets), 60000);
            } else {
              setLoading(false);
              setDqJobStatusLoading(false);
            }
          })
          } catch (err) {
              setError("Failed to poll dq rule job status.");
              setLoading(false);
              setDqJobStatusLoading(false);
          }
      };

    const handleRunDQ = async () => {
      try {
        setRunDqLoading(true);
        const BASE_URL = process.env.REACT_APP_API_URL;
        await axios.post(`${BASE_URL}/runQuality/product/${id}`);
        toast.success('DQ checks running successfully!');
        setHasResultsFetched(false);
        setTimeout(() => {
                    setDqJobStatusLoading(true);
                    // Trigger the polling flow by calling pollStatus
                    pollStatus(assets);
                }, 2000);
      } catch (err) {
        toast.error('Failed to run DQ checks. Please try again.');
      } finally {
        setRunDqLoading(false);
      }
    };

    const handleViewResult = async (assetId) => {
      const resultByAsset = dataQualityResults.find(res => res.data_asset_id === assetId) || null;   
      const filteredRules = resultByAsset?.rules?.filter((rule) => rule.rule_name !== "row_count") || null;
      const total = resultByAsset?.rules?.length || 0;
      const completed = resultByAsset?.rules?.filter(rule => rule.dq_result !== "pass")?.length|| 0;
      const score = resultByAsset?.latest_dimension_scores.score|| 0;
      const dimensionWeightage = resultByAsset?.latest_dimension_scores || null;
      const chart = resultByAsset?.score_trend_plot_config || null;
      const updatedWeightage = { ...dimensionWeightage }; 
      delete updatedWeightage.score; 
      
      setDQResultAsset({ ...resultByAsset, rules :filteredRules });
      setTotalRecords(total);
      setCompletedRecords(completed);
      setQualityScore(score);
      setWeightage(updatedWeightage);
      setChartData(chart);
    };

    const fetchRuleSuggestions = async (assetId) => {
      try {
        setRuleSuggestionsLoading(true);
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/rule-suggestions/${assetId}`);
        if (response?.status === 200) {
          setRuleSuggestions(response.data);
          setOpenRuleSuggestionsModal(true);
        }
      } catch (err) {
        console.error('Error fetching rule suggestions:', err);
        toast.error('Failed to fetch rule suggestions. Please try again.');
      } finally {
        setRuleSuggestionsLoading(false);
      }
    };

    console.log("loading  vlaue", loading)

    if(loading){
      return(
        <div className="fixed inset-0 bg-white flex items-center justify-center z-50 w-full h-full">
                   <div className="flex flex-col items-center space-y-3">
                     <img src={preloader} alt="Loading..." width={200} />
                     <span className="text-gray-700 text-[20px]">
                     Hang tight! The resources are loading...
                     </span>
                   </div>
                 </div>
      )
    }

    if (dqJobStatusLoading) {
            return (
                <Paper elevation={3} sx={{ p: 4, maxWidth: 700, margin: 'auto', mt: 16, border: "1px solid #dacdcd" }}>
                    <Box sx={{ textAlign: 'center' }}>
                        <img src={preloader} alt='loading' width={200} className=' block m-auto animate-pulse'/>
                        <Typography variant="h5" component="h2" sx={{ mt: 2, mb: 1 }}>
                            DQ check in Progress
                        </Typography>
                        <Typography color="text.secondary" sx={{ mb: 3 }}>
                            Please wait while we analyze the data quality rules. This might take a few moments.
                        </Typography>
                        <LinearProgress />
                    </Box>
                </Paper>
            );
        }
  return (
    <>
    <div className='w-[90%] m-auto mt-6'>
    <TableContainer 
      component={Paper} 
      sx={{ 
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)', 
        border: '1px solid #e0e0e0', 
        borderRadius: '12px',
        maxHeight: '500px',
        overflow: 'auto'
      }}
    >
      <Table sx={{ minWidth: 650 }} stickyHeader>
        <TableHead>
          {finalResult ? (
          <TableRow>
            <TableCell sx={{ 
              color: "white", 
              fontWeight: 600, 
              fontSize: '14px',
              padding: '16px 20px',
              borderBottom: '2px solid #000080',
              position: 'sticky',
              top: 0,
              zIndex: 2,
              backgroundColor: '#0000a0 !important'
            }}>
              Asset Name
            </TableCell>
            <TableCell sx={{ 
              color: "white", 
              fontWeight: 600, 
              fontSize: '14px',
              padding: '16px 20px',
              borderBottom: '2px solid #000080',
              position: 'sticky',
              top: 0,
              zIndex: 2,
              backgroundColor: '#0000a0 !important'
            }}>
              Recommended Rules
            </TableCell>
            <TableCell sx={{ 
              color: "white", 
              fontWeight: 600, 
              fontSize: '14px',
              padding: '16px 20px',
              borderBottom: '2px solid #000080',
              position: 'sticky',
              top: 0,
              zIndex: 2,
              backgroundColor: '#0000a0 !important'
            }}>
              Configuration
            </TableCell>
            <TableCell sx={{ 
              color: "white", 
              fontWeight: 600, 
              fontSize: '14px',
              padding: '16px 20px',
              borderBottom: '2px solid #000080',
              position: 'sticky',
              top: 0,
              zIndex: 2,
              backgroundColor: '#0000a0 !important'
            }}>
              DQ Job Status
            </TableCell>
            <TableCell sx={{ 
              color: "white", 
              fontWeight: 600, 
              fontSize: '14px',
              padding: '16px 20px',
              borderBottom: '2px solid #000080',
              position: 'sticky',
              top: 0,
              zIndex: 2,
              backgroundColor: '#0000a0 !important'
            }}>
              View Result
            </TableCell>
          </TableRow>
          ):
          (
            <TableRow>
            <TableCell sx={{ 
              color: "white", 
              fontWeight: 600, 
              fontSize: '14px',
              padding: '16px 20px',
              borderBottom: '2px solid #000080',
              position: 'sticky',
              top: 0,
              zIndex: 2,
              backgroundColor: '#0000a0 !important'
            }}>
              Asset Name
            </TableCell>
            <TableCell sx={{ 
              color: "white", 
              fontWeight: 600, 
              fontSize: '14px',
              padding: '16px 20px',
              borderBottom: '2px solid #000080',
              position: 'sticky',
              top: 0,
              zIndex: 2,
              backgroundColor: '#0000a0 !important'
            }}>
              Recommended Rules
            </TableCell>
            <TableCell sx={{ 
              color: "white", 
              fontWeight: 600, 
              fontSize: '14px',
              padding: '16px 20px',
              borderBottom: '2px solid #000080',
              position: 'sticky',
              top: 0,
              zIndex: 2,
              backgroundColor: '#0000a0 !important'
            }}>
              Configuration
            </TableCell>
          </TableRow>
          )
          }
        </TableHead>
        <TableBody>
          {finalResult ? (
          assets.map((row, index) => (           
            <TableRow 
              key={row.id}
              sx={{ 
                backgroundColor: index % 2 === 0 ? '#fafafa' : '#ffffff',
                '&:hover': {
                  backgroundColor: '#f0f8ff',
                  transition: 'background-color 0.2s ease'
                }
              }}
            >
              <TableCell sx={{ 
                padding: '16px 20px',
                fontWeight: 600,
                fontSize: '14px',
                color: '#2c3e50'
              }}>
                {row.name}
              </TableCell>
              <TableCell sx={{ 
                padding: '16px 20px',
                fontSize: '14px',
                color: '#555'
              }}>
                <Button 
                  variant="outlined" 
                  size="small"
                  onClick={() => fetchRuleSuggestions(row.id)}
                  disabled={ruleSuggestionsLoading}
                  sx={{ 
                    color: '#0000a0', 
                    borderColor: '#0000a0',
                    fontWeight: 600,
                    '&:hover': {
                      backgroundColor: '#f0f8ff',
                      borderColor: '#0000a0'
                    },
                    '&:disabled': {
                      color: '#9ca3af',
                      borderColor: '#9ca3af'
                    }
                  }}
                >
                  {ruleSuggestionsLoading ? 'Loading...' : 'View Rules'}
                </Button>
              </TableCell>
              <TableCell sx={{ 
                padding: '16px 20px',
                fontSize: '14px'
              }}>
                <IconButton 
                  aria-label="settings"  
                  onClick={() => {
                    setAssetId(row.id)
                    setAssetName(row.name)
                    setOpenModal(true)
                  }}
                  sx={{ 
                    color: "#0000a0",
                    '&:hover': {
                      backgroundColor: '#f0f8ff'
                    }
                  }}
                >
                  <SettingsIcon />
                </IconButton>
              </TableCell>
              <TableCell sx={{ 
                padding: '16px 20px',
                fontSize: '14px'
              }}>
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    px: 2,
                    py: 0.5,
                    borderRadius: '20px',
                    fontSize: '12px',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    backgroundColor: row.dq_job_status === "Completed" ? '#e8f5e8' : row.dq_job_status === "Failed" ? '#ffeaea' : '#fff3e0',
                    color: row.dq_job_status === "Completed" ? '#2e7d32' : row.dq_job_status === "Failed" ? '#d32f2f' : '#f57c00'
                  }}
                >
                  {row.dq_job_status || 'Pending'}
                </Box>
              </TableCell>
              <TableCell sx={{ 
                padding: '16px 20px',
                fontSize: '14px'
              }}>
                <Button 
                  variant="text" 
                  sx={{ 
                    color: '#0000a0', 
                    fontWeight: 600,
                    '&:hover': {
                      backgroundColor: '#f0f8ff'
                    }
                  }} 
                  onClick={() => { 
                    handleViewResult(row.id);
                    setOpenResultModal(true); 
                  }}
                >
                  View Result
                </Button>
              </TableCell>
            </TableRow>
          ))
          ):(
            assets.map((row, index) => (           
            <TableRow 
              key={row.id}
              sx={{ 
                backgroundColor: index % 2 === 0 ? '#fafafa' : '#ffffff',
                '&:hover': {
                  backgroundColor: '#f0f8ff',
                  transition: 'background-color 0.2s ease'
                }
              }}
            >
              <TableCell sx={{ 
                padding: '16px 20px',
                fontWeight: 600,
                fontSize: '14px',
                color: '#2c3e50'
              }}>
                {row.name}
              </TableCell>
              <TableCell sx={{ 
                padding: '16px 20px',
                fontSize: '14px',
                color: '#555'
              }}>
                <Button 
                  variant="outlined" 
                  size="small"
                  onClick={() => fetchRuleSuggestions(row.id)}
                  disabled={ruleSuggestionsLoading}
                  sx={{ 
                    color: '#0000a0', 
                    borderColor: '#0000a0',
                    fontWeight: 600,
                    '&:hover': {
                      backgroundColor: '#f0f8ff',
                      borderColor: '#0000a0'
                    },
                    '&:disabled': {
                      color: '#9ca3af',
                      borderColor: '#9ca3af'
                    }
                  }}
                >
                  {ruleSuggestionsLoading ? 'Loading...' : 'View Rules'}
                </Button>
              </TableCell>
              <TableCell sx={{ 
                padding: '16px 20px',
                fontSize: '14px'
              }}>
                <IconButton 
                  aria-label="settings"  
                  onClick={() => {
                    setAssetId(row.id)
                    setAssetName(row.name)
                    setOpenModal(true)
                  }}
                  sx={{ 
                    color: "#0000a0",
                    '&:hover': {
                      backgroundColor: '#f0f8ff'
                    }
                  }}
                >
                  <SettingsIcon />
                </IconButton>
              </TableCell>
            </TableRow>
          ))
          )}
        </TableBody>
      </Table>
    </TableContainer>

    {openResultModal && dqResultAsset ? (
      <Dialog open={openResultModal} onClose={() => setOpenResultModal(false)} maxWidth="xl" fullWidth>
        <div className='flex justify-between items-center '>
          <DialogTitle sx={{ fontWeight: 700, fontSize: 20 }}>DQ Results</DialogTitle>
          <IconButton sx={{marginRight: '20px'}} onClick={() => setOpenResultModal(false) } >
            <CloseIcon />
          </IconButton>
        </div>
        <DialogContent>
          <div>
            {/* Disclaimer Section */}
            <Box
              sx={{
                backgroundColor: '#fff3cd',
                border: '1px solid #ffeaa7',
                borderRadius: '12px',
                padding: '16px 20px',
                marginBottom: '24px',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '12px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
              }}
            >
              <Box
                sx={{
                  color: '#856404',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minWidth: '24px',
                  height: '24px',
                  borderRadius: '50%',
                  backgroundColor: '#fff3cd',
                  border: '2px solid #856404'
                }}
              >
                <Typography
                  sx={{
                    fontSize: '14px',
                    fontWeight: 700,
                    lineHeight: 1
                  }}
                >
                  ⚠
                </Typography>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography
                  sx={{
                    fontSize: '14px',
                    fontWeight: 600,
                    color: '#856404',
                    marginBottom: '4px',
                    lineHeight: 1.4
                  }}
                >
                  Disclaimer
                </Typography>
                <Typography
                  sx={{
                    fontSize: '13px',
                    color: '#856404',
                    lineHeight: 1.5,
                    opacity: 0.9
                  }}
                >
                  If there are no other rules apart from row count and schema check, results will not be available. Please add more rules to see complete results.
                </Typography>
              </Box>
            </Box>

            {/* Stats Cards Row */}
            <Box sx={{ display: 'flex', gap: 4, mb: 3 }}>
              <Box
                sx={{
                  backgroundColor: '#fff',
                  borderRadius: '16px',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
                  padding: 3,
                  width: '200px',
                  height: '120px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  border: '1px solid #d3d3d3',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                  '&:hover': {
                    boxShadow: '0 12px 30px rgba(0, 0, 0, 0.2) !important',
                    transform: 'translateY(-6px) !important',
                    border: '1px solid #0000a0 !important',
                    backgroundColor: '#f8f9ff'
                  }
                }}
              >
                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 600, color: '#0000a0', fontSize: '1.1rem' }}
                  >
                    DQ Score
                  </Typography>
                  <Box
                    sx={{
                      height: '3px',
                      width: '40px',
                      backgroundColor: '#0000a0',
                      borderRadius: '2px',
                      mt: 0.5,
                    }}
                  />
                </Box>
                <Typography
                  variant="h4"
                  sx={{color: getQualityScoreColor(qualityScore), fontWeight: 700, fontSize: '2rem'}}
                >
                  {qualityScore}
                </Typography>
              </Box>
              <Box
                sx={{
                  backgroundColor: '#fff',
                  borderRadius: '16px',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
                  padding: 3,
                  width: '200px',
                  height: '120px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  border: '1px solid #d3d3d3',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                  '&:hover': {
                    boxShadow: '0 12px 30px rgba(0, 0, 0, 0.2) !important',
                    transform: 'translateY(-6px) !important',
                    border: '1px solid #0000a0 !important',
                    backgroundColor: '#f8f9ff'
                  }
                }}
              >
                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 600, color: '#0000a0', fontSize: '1.1rem' }}
                  >
                    Rules Processed
                  </Typography>
                  <Box
                    sx={{
                      height: '3px',
                      width: '40px',
                      backgroundColor: '#0000a0',
                      borderRadius: '2px',
                      mt: 0.5,
                    }}
                  />
                </Box>
                <Typography
                  variant="h4"
                  sx={{ fontWeight: 700, color: '#3C3C3C', fontSize: '2rem' }}
                >
                  {totalRecords}
                </Typography>
              </Box>
              <Box
                sx={{
                  backgroundColor: '#fff',
                  borderRadius: '16px',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
                  padding: 3,
                  width: '200px',
                  height: '120px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  border: '1px solid #d3d3d3',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                  '&:hover': {
                    boxShadow: '0 12px 30px rgba(0, 0, 0, 0.2) !important',
                    transform: 'translateY(-6px) !important',
                    border: '1px solid #0000a0 !important',
                    backgroundColor: '#f8f9ff'
                  }
                }}
              >
                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 600, color: '#0000a0', fontSize: '1.1rem' }}
                  >
                    Rules Failed
                  </Typography>
                  <Box
                    sx={{
                      height: '3px',
                      width: '40px',
                      backgroundColor: '#0000a0',
                      borderRadius: '2px',
                      mt: 0.5,
                    }}
                  />
                </Box>
                <Typography
                  variant="h4"
                  sx={{ fontWeight: 700, color: '#3C3C3C', fontSize: '2rem' }}
                >
                  {completedRecords}
                </Typography>
              </Box>
            </Box>

            {/* Two Column Layout */}
            <Box sx={{ display: 'flex', gap: 4, mb: 3 }}>
              {/* Left Column - Weightage */}
              <Box 
                sx={{ 
                  backgroundColor: '#fff',
                  borderRadius: '16px',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
                  padding: 3,
                  border: '1px solid #d3d3d3',
                  flex: 1,
                  minWidth: 0
                }}
              >
                <Typography 
                  variant="h6" 
                  sx={{
                    color: '#0000a0',
                    fontWeight: 700,
                    fontSize: '1.1rem',
                    marginBottom: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}
                >
                  <Box
                    sx={{
                      width: '4px',
                      height: '20px',
                      backgroundColor: '#0000a0',
                      borderRadius: '2px'
                    }}
                  />
                  Dimension Level Score
                </Typography>
                <Box sx={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                  gap: 3,
                  maxWidth: '100%'
                }}>
                  {weightage && Object.entries(weightage)
                    .filter(([key]) => key.endsWith('_weightage')) // Only process weightage entries
                    .map(([key, value]) => {
                      // Extract score and weightage from the API response structure
                      const dimensionName = key.replace('_weightage', '');
                      const score = dqResultAsset?.latest_dimension_scores?.[dimensionName] || 0;
                      const weightageValue = value;
                      const contribution = score > 0 && weightageValue > 0 ? (score * weightageValue / 100).toFixed(2) : 0;
                      
                      return (
                        <Box 
                          key={key} 
                          sx={{
                             backgroundColor: weightageValue > 0 ? '#f0f9ff' : '#e5e2e2',
                            borderRadius: '12px',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                            padding: 3,
                            border: '1px solid',
                            borderColor: weightageValue > 0 ? '#e0f2fe' : '#e5e2e2',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              boxShadow: '0 4px 16px rgba(0, 0, 0, 0.12)',
                              transform: 'translateY(-2px)'
                            }
                          }}
                        >
                          <Typography 
                            variant="h6" 
                            sx={{
                              fontWeight: 700,
                              fontSize: '1rem',
                              color: '#1f2937',
                              marginBottom: 2,
                              textTransform: 'uppercase',
                              textAlign: 'center'
                            }}
                          >
                            {dimensionName.charAt(0).toUpperCase() + dimensionName.slice(1)}
                          </Typography>
                          
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {/* Score */}
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography 
                                variant="body2" 
                                sx={{
                                  fontWeight: 600,
                                  fontSize: '0.875rem',
                                  color: '#6b7280'
                                }}
                              >
                                Score
                              </Typography>
                              <Typography 
                                sx={{
                                  fontWeight: 700,
                                  fontSize: '1.1rem',
                                  color: score > 0 ? '#059669' : '#dc2626'
                                }}
                              >
                                {score}
                              </Typography>
                            </Box>
                            
                            {/* Weightage */}
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography 
                                variant="body2" 
                                sx={{
                                  fontWeight: 600,
                                  fontSize: '0.875rem',
                                  color: '#6b7280'
                                }}
                              >
                                Weightage
                              </Typography>
                              <Typography 
                                sx={{
                                  fontWeight: 700,
                                  fontSize: '1.1rem',
                                  color: '#374151'
                                }}
                              >
                                {weightageValue}%
                              </Typography>
                            </Box>
                            
                            {/* Contribution */}
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography 
                                variant="body2" 
                                sx={{
                                  fontWeight: 600,
                                  fontSize: '0.875rem',
                                  color: '#6b7280'
                                }}
                              >
                                Contribution
                              </Typography>
                              <Typography 
                                sx={{
                                  fontWeight: 700,
                                  fontSize: '1.1rem',
                                  color: score > 0 ? '#059669' : '#dc2626'
                                }}
                              >
                                {contribution}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      );
                    })}
                </Box>
              </Box>

              {/* Right Column - Chart */}
              <Box 
                sx={{
                  backgroundColor: '#fff',
                  borderRadius: '16px',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
                  padding: 3,
                  border: '1px solid #d3d3d3',
                  flex: 1,
                  minWidth: 0
                }}
              >
                <Typography 
                  variant="h6" 
                  sx={{
                    color: '#0000a0',
                    fontWeight: 700,
                    fontSize: '1.1rem',
                    marginBottom: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}
                >
                  <Box
                    sx={{
                      width: '4px',
                      height: '20px',
                      backgroundColor: '#0000a0',
                      borderRadius: '2px'
                    }}
                  />
                  DQ Score Trend
                </Typography>
                <Box sx={{ width: '100%', height: '300px' }}>
                  <Plot 
                    data={chartData?.data || []}
                    layout={{
                      ...(chartData?.layout || {}),
                      margin: { t: 40, b: 60, l: 60, r: 40 },
                      autosize: true,
                      height: 280
                    }}
                    style={{ width: '100%', height: '100%' }}
                    useResizeHandler={true}
                  />
                </Box>
              </Box>
            </Box>

            <TableContainer 
              component={Paper} 
              sx={{ 
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)', 
                border: '1px solid #e0e0e0', 
                borderRadius: '12px',
                marginTop: "30px",
                maxHeight: '500px',
                overflow: 'auto'
              }}
            >
              <Table sx={{ minWidth: 650 }} stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ 
                      color: "white", 
                      fontWeight: 600, 
                      fontSize: '14px',
                      padding: '16px 20px',
                      borderBottom: '2px solid #000080',
                      position: 'sticky',
                      top: 0,
                      zIndex: 2,
                      backgroundColor: '#0000a0 !important'
                    }}>
                      Column Name
                    </TableCell>
                    <TableCell sx={{ 
                      color: "white", 
                      fontWeight: 600, 
                      fontSize: '14px',
                      padding: '16px 20px',
                      borderBottom: '2px solid #000080',
                      position: 'sticky',
                      top: 0,
                      zIndex: 2,
                      backgroundColor: '#0000a0 !important'
                    }}>
                      Rule Description
                    </TableCell>
                    <TableCell sx={{ 
                      color: "white", 
                      fontWeight: 600, 
                      fontSize: '14px',
                      padding: '16px 20px',
                      borderBottom: '2px solid #000080',
                      position: 'sticky',
                      top: 0,
                      zIndex: 2,
                      backgroundColor: '#0000a0 !important'
                    }}>
                      Dimension
                    </TableCell>
                    <TableCell sx={{ 
                      color: "white", 
                      fontWeight: 600, 
                      fontSize: '14px',
                      padding: '16px 20px',
                      borderBottom: '2px solid #000080',
                      position: 'sticky',
                      top: 0,
                      zIndex: 2,
                      backgroundColor: '#0000a0 !important'
                    }}>
                      Keywords
                    </TableCell>
                    <TableCell sx={{ 
                      color: "white", 
                      fontWeight: 600, 
                      fontSize: '14px',
                      padding: '16px 20px',
                      borderBottom: '2px solid #000080',
                      position: 'sticky',
                      top: 0,
                      zIndex: 2,
                      backgroundColor: '#0000a0 !important'
                    }}>
                      Status
                    </TableCell>
                    <TableCell sx={{ 
                      color: "white", 
                      fontWeight: 600, 
                      fontSize: '14px',
                      padding: '16px 20px',
                      borderBottom: '2px solid #000080',
                      position: 'sticky',
                      top: 0,
                      zIndex: 2,
                      backgroundColor: '#0000a0 !important'
                    }}>
                      Score
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dqResultAsset?.rules?.length > 0 ? (
                    dqResultAsset.rules.map((rule, index) => (
                      <TableRow 
                        key={rule.rule_name}
                        sx={{ 
                          backgroundColor: index % 2 === 0 ? '#fafafa' : '#ffffff',
                          '&:hover': {
                            backgroundColor: '#f0f8ff',
                            transition: 'background-color 0.2s ease'
                          }
                        }}
                      >
                        <TableCell sx={{ 
                          padding: '16px 20px',
                          fontWeight: 600,
                          fontSize: '14px',
                          color: '#2c3e50'
                        }}>
                          {rule.check_type === "Asset Level Check" ? "Asset Level Rule" : rule.column_name}
                        </TableCell>
                        <TableCell sx={{ 
                          padding: '16px 20px',
                          fontSize: '14px',
                          color: '#555',
                          maxWidth: '300px',
                          wordWrap: 'break-word'
                        }}>
                          {rule.rule_description}
                        </TableCell>
                        <TableCell sx={{ 
                          padding: '16px 20px',
                          fontSize: '14px',
                          color: '#555',
                          textTransform: 'capitalize'
                        }}>
                          {rule.dimension_name.charAt(0).toUpperCase() + rule.dimension_name.slice(1)}
                        </TableCell>
                        <TableCell sx={{ 
                          padding: '16px 20px',
                          fontSize: '14px',
                          color: '#555',
                          textTransform: 'capitalize'
                        }}>
                          {rule.keywords}
                        </TableCell>
                        <TableCell sx={{ 
                          padding: '16px 20px',
                          fontSize: '14px'
                        }}>
                          <Box
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              px: 2,
                              py: 0.5,
                              borderRadius: '20px',
                              fontSize: '12px',
                              fontWeight: 600,
                              textTransform: 'uppercase',
                              backgroundColor: rule.dq_result === "pass" ? '#e8f5e8' : '#ffeaea',
                              color: rule.dq_result === "pass" ? '#2e7d32' : '#d32f2f'
                            }}
                          >
                            {rule.dq_result === "pass" ? "Pass" : rule.dq_result === "fail" ? "Fail" : rule.dq_result === "FAILURE" ? "Not Run" : "N/A"}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ 
                          padding: '16px 20px',
                          fontSize: '14px',
                          fontWeight: 600,
                          color: getQualityScoreColor(rule.quality_score)
                        }}>
                          {rule.quality_score}%
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell 
                        colSpan={5} 
                        align="center"
                        sx={{ 
                          padding: '40px 20px',
                          fontSize: '16px',
                          color: '#666',
                          fontStyle: 'italic'
                        }}
                      >
                        No rules found for the selected dataset.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </div>
        </DialogContent>
      </Dialog>
    ) : null}

    <div className="flex justify-end space-x-4 mt-8">
                <button
                  onClick={handleRunDQ}
                  disabled={runDqLoading}
                  className={`flex items-center space-x-2 px-6 py-3 rounded-lg transition-colors duration-200 shadow-md ${
                    runDqLoading 
                      ? 'bg-gray-400 text-gray-600 cursor-not-allowed' 
                      : 'bg-[#0000a0] text-white hover:bg-[#0000a0]'
                  }`}
                >
                  <Save size={20} />
                  <span>{runDqLoading ? 'Loading...' : 'Run DQ Checks'}</span>
                </button>
                {(() => {
                  const dataEngineerVendorRoles = ['Data Engineer', 'Vendor'];
                  const isDisabled = dataEngineerVendorRoles.includes(userRole) || !dataQualityResults;
                  const disabledReason = dataEngineerVendorRoles.includes(userRole) ? 
                  "You are not allowed to go to publish page" : "DQ check has not run yet";
                  
                  return (
                    <Tooltip 
                      title={isDisabled ? disabledReason : ""}
                      placement="top"
                      arrow
                    >
                      <span>
                        <button
                          onClick={() => nextStep()}
                          disabled={isDisabled}
                          className={`flex items-center space-x-2 px-6 py-3 rounded-lg transition-colors duration-200 shadow-md ${
                            isDisabled 
                              ? 'bg-gray-400 text-gray-600 cursor-not-allowed' 
                              : 'bg-[#0000a0] text-white hover:bg-[#0000a0]'
                          }`}
                        >
                          <Check size={20} />
                          <span>Ready to Publish?</span>
                        </button>
                      </span>
                    </Tooltip>
                  );
                })()}
              </div>

              {/* Note Section */}
              <Box
                sx={{
                  backgroundColor: '#fff3cd',
                  border: '1px solid #ffeaa7',
                  borderRadius: '12px',
                  padding: '16px 20px',
                  marginTop: '24px',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '12px',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
                }}
              >
                <Box
                  sx={{
                    color: '#856404',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: '24px',
                    height: '24px',
                    borderRadius: '50%',
                    backgroundColor: '#fff3cd',
                    border: '2px solid #856404',
                    flexShrink: 0
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '14px',
                      fontWeight: 700,
                      lineHeight: 1
                    }}
                  >
                    ⚠
                  </Typography>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    sx={{
                      fontSize: '14px',
                      fontWeight: 600,
                      color: '#856404',
                      marginBottom: '4px',
                      lineHeight: 1.4
                    }}
                  >
                    Note
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '13px',
                      color: '#856404',
                      lineHeight: 1.5,
                      opacity: 0.9
                    }}
                  >
                    To get Error DataFrames from the asset ingestion pipelines, configure the pipeline to generate an output DataFrame and name it using the format - <strong>{'{asset_name}_output_df'}</strong>
                  </Typography>
                </Box>
              </Box>
    </div>

    {openModal  && (
        <DataQualityMain setOpenModal={setOpenModal} assetId={assetId} assetName={assetName} productId={id} />
    )}

    {/* Rule Suggestions Modal */}
    {openRuleSuggestionsModal && (
      <Dialog open={openRuleSuggestionsModal} onClose={() => setOpenRuleSuggestionsModal(false)} maxWidth="lg" fullWidth>
        <div className='flex justify-between items-center'>
          <DialogTitle sx={{ fontWeight: 700, fontSize: 20 }}>Recommended Rules for {assetName}</DialogTitle>
          <IconButton sx={{marginRight: '20px'}} onClick={() => setOpenRuleSuggestionsModal(false)}>
            <CloseIcon />
          </IconButton>
        </div>
        <DialogContent>
          <TableContainer 
            component={Paper} 
            sx={{ 
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)', 
              border: '1px solid #e0e0e0', 
              borderRadius: '12px',
              maxHeight: '500px',
              overflow: 'auto'
            }}
          >
            <Table sx={{ minWidth: 650 }} stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ 
                    color: "white", 
                    fontWeight: 600, 
                    fontSize: '14px',
                    padding: '16px 20px',
                    borderBottom: '2px solid #000080',
                    position: 'sticky',
                    top: 0,
                    zIndex: 2,
                    backgroundColor: '#0000a0 !important'
                  }}>
                    Column Name
                  </TableCell>
                  <TableCell sx={{ 
                    color: "white", 
                    fontWeight: 600, 
                    fontSize: '14px',
                    padding: '16px 20px',
                    borderBottom: '2px solid #000080',
                    position: 'sticky',
                    top: 0,
                    zIndex: 2,
                    backgroundColor: '#0000a0 !important'
                  }}>
                    Suggested Rule
                  </TableCell>
                  <TableCell sx={{ 
                    color: "white", 
                    fontWeight: 600, 
                    fontSize: '14px',
                    padding: '16px 20px',
                    borderBottom: '2px solid #000080',
                    position: 'sticky',
                    top: 0,
                    zIndex: 2,
                    backgroundColor: '#0000a0 !important'
                  }}>
                    Description
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {ruleSuggestions.length > 0 ? (
                  ruleSuggestions.map((rule, index) => (
                    <TableRow 
                      key={`${rule.column_name}-${rule.rule_suggestions}-${index}`}
                      sx={{ 
                        backgroundColor: index % 2 === 0 ? '#fafafa' : '#ffffff',
                        '&:hover': {
                          backgroundColor: '#f0f8ff',
                          transition: 'background-color 0.2s ease'
                        }
                      }}
                    >
                      <TableCell sx={{ 
                        padding: '16px 20px',
                        fontWeight: 600,
                        fontSize: '14px',
                        color: '#2c3e50'
                      }}>
                        {rule.column_name}
                      </TableCell>
                      <TableCell sx={{ 
                        padding: '16px 20px',
                        fontSize: '14px',
                        color: '#555',
                        fontFamily: 'monospace',
                        backgroundColor: '#f8f9fa',
                        borderRadius: '4px',
                        padding: '8px 12px'
                      }}>
                        {rule.rule_suggestions}
                      </TableCell>
                      <TableCell sx={{ 
                        padding: '16px 20px',
                        fontSize: '14px',
                        color: '#555',
                        maxWidth: '400px',
                        wordWrap: 'break-word'
                      }}>
                        {rule.description}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell 
                      colSpan={3} 
                      align="center"
                      sx={{ 
                        padding: '40px 20px',
                        fontSize: '16px',
                        color: '#666',
                        fontStyle: 'italic'
                      }}
                    >
                      {ruleSuggestionsLoading ? 'Loading rule suggestions...' : 'No rule suggestions available for this asset.'}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
      </Dialog>
    )}

    <Toaster position="top-right" />
    </>
  );
}
